import { AlertTriangle, Check, FileSpreadsheet, Upload } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import type { UnitOfMeasure } from '@obraplan/core/domain/schedule/unit-of-measure';
import {
  SCHEDULE_IMPORT_COLUMNS,
  SCHEDULE_ROW_STATUS_LABEL,
  type ScheduleColumnMapping,
  type ScheduleImportField,
  type ScheduleImportOptions,
  type ScheduleImportPlan,
  type ScheduleRowStatus,
} from '@obraplan/core/domain/schedule/schedule-import';
import type {
  ImportView,
  ValidatedImport,
} from '@obraplan/core/application/use-cases/schedule/schedule-import-use-cases';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { cn } from '@/lib/cn';

const STATUS_TONE: Readonly<Record<ScheduleRowStatus, 'ok' | 'warn' | 'danger' | 'neutral'>> = {
  VALID: 'ok',
  WARNING: 'warn',
  INVALID: 'danger',
  DUPLICATE: 'neutral',
};

const STEPS = ['Arquivo', 'Colunas', 'Conferência', 'Resultado'] as const;

export interface ImportResultSummary {
  readonly createdEntries: number;
  readonly updatedEntries: number;
  readonly createdTowers: number;
  readonly createdFloors: number;
  readonly createdActivities: number;
  readonly createdMaterials: number;
  readonly ignoredRows: number;
}

export interface ScheduleImportWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  units: readonly UnitOfMeasure[];
  started: ImportView | undefined;
  validated: ValidatedImport | undefined;
  result: ImportResultSummary | undefined;
  onFile: (file: File) => void;
  onValidate: (mapping: ScheduleColumnMapping, options: ScheduleImportOptions) => void;
  onConfirm: () => void;
  isPending: boolean;
  errorMessage: string | undefined;
}

/**
 * Assistente de importação do cronograma.
 *
 * Quatro passos, e o terceiro é o que justifica os outros três: **nada é gravado até a
 * confirmação**. Quem importa vê antes quantas linhas entram, quantas atualizam, o que
 * seria criado de estrutura e exatamente por que cada linha recusada foi recusada — com
 * o número da linha, o valor recebido e o motivo em português.
 */
export function ScheduleImportWizard({
  open,
  onOpenChange,
  units,
  started,
  validated,
  result,
  onFile,
  onValidate,
  onConfirm,
  isPending,
  errorMessage,
}: ScheduleImportWizardProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [mapping, setMapping] = useState<ScheduleColumnMapping>({});
  const [createStructure, setCreateStructure] = useState(false);
  const [createMaterials, setCreateMaterials] = useState(false);
  const [fallbackUnitId, setFallbackUnitId] = useState('');
  const [filter, setFilter] = useState<ScheduleRowStatus | 'ALL'>('ALL');

  const step =
    result !== undefined ? 3 : validated !== undefined ? 2 : started !== undefined ? 1 : 0;
  const effectiveMapping = Object.keys(mapping).length > 0 ? mapping : (started?.mapping ?? {});

  const missingRequired = SCHEDULE_IMPORT_COLUMNS.filter(
    (column) => column.required && effectiveMapping[column.field] === undefined,
  );

  const options: ScheduleImportOptions = {
    createMissingStructure: createStructure,
    createMissingMaterials: createMaterials,
    ...(fallbackUnitId === '' ? {} : { fallbackUnitId }),
  };

  const plan: ScheduleImportPlan | undefined = validated?.plan;
  const visibleRows = useMemo(() => {
    if (plan === undefined) return [];
    const rows = filter === 'ALL' ? plan.rows : plan.rows.filter((row) => row.status === filter);
    return rows.slice(0, 200);
  }, [plan, filter]);

  const changeMapping = (field: ScheduleImportField, header: string): void => {
    const next: ScheduleColumnMapping = { ...effectiveMapping };
    if (header === '') delete next[field];
    else next[field] = header;
    setMapping(next);
  };

  /** Leva as linhas recusadas para um CSV, para correção offline. */
  const downloadErrors = (): void => {
    if (plan === undefined) return;
    const problems = plan.rows.filter(
      (row) => row.status === 'INVALID' || row.status === 'DUPLICATE',
    );
    const header = 'Linha;Torre;Pavimento;Disciplina;Material;Quantidade;Necessário em;Motivo';
    const body = problems.map((row) =>
      [
        row.rowNumber,
        row.raw.tower,
        row.raw.floor,
        row.raw.discipline,
        row.raw.material,
        row.raw.quantity,
        row.raw.requiredDate,
        row.issues.map((issue) => issue.message).join(' '),
      ]
        .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
        .join(';'),
    );

    // BOM na frente: sem ele o Excel abre o CSV acentuado como se fosse Latin-1.
    const blob = new Blob([`\u{FEFF}${[header, ...body].join('\n')}`], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `linhas-com-erro-${started?.record.fileName ?? 'cronograma'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Importar cronograma</DialogTitle>
          <DialogDescription>
            A planilha só altera a obra depois que você confirmar. Até lá, nada é gravado.
          </DialogDescription>
        </DialogHeader>

        {/* ── Trilha dos passos ─────────────────────────────────────────────── */}
        <ol className="mb-4 flex flex-wrap gap-2 text-xs">
          {STEPS.map((label, index) => (
            <li
              key={label}
              aria-current={index === step ? 'step' : undefined}
              className={cn(
                'flex items-center gap-1.5 rounded-full border px-3 py-1',
                index === step
                  ? 'border-(--color-brand) font-medium text-(--color-brand)'
                  : 'border-(--color-border-subtle) text-(--color-content-muted)',
              )}
            >
              {index < step ? <Check className="size-3" aria-hidden="true" /> : `${index + 1}.`}
              {label}
            </li>
          ))}
        </ol>

        <div className="flex flex-col gap-4">
          {/* ── 1. Arquivo ─────────────────────────────────────────────────── */}
          {step === 0 && (
            <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-(--color-border-subtle) p-8 text-center">
              <FileSpreadsheet className="size-8 text-(--color-content-muted)" aria-hidden="true" />
              <p className="text-sm text-(--color-content-muted)">
                Escolha a planilha do cronograma em CSV ou XLSX. A primeira linha deve ter os
                títulos das colunas — torre, pavimento, disciplina, material, quantidade e a data em
                que o material é necessário.
              </p>
              <input
                ref={fileInput}
                type="file"
                accept=".csv,.tsv,.txt,.xlsx"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file !== undefined) onFile(file);
                  event.target.value = '';
                }}
              />
              <Button type="button" variant="primary" onClick={() => fileInput.current?.click()}>
                <Upload aria-hidden="true" />
                Escolher planilha
              </Button>
            </div>
          )}

          {/* ── 2. Colunas ─────────────────────────────────────────────────── */}
          {step === 1 && started !== undefined && (
            <>
              <p className="text-sm">
                <strong>{started.record.fileName}</strong>{' '}
                <span className="text-(--color-content-muted)">
                  · {started.record.sourceFormat} · {started.record.totalRows} linhas
                </span>
              </p>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {SCHEDULE_IMPORT_COLUMNS.map((column) => (
                  <Field
                    key={column.field}
                    label={column.label}
                    htmlFor={`sched-map-${column.field}`}
                    required={column.required}
                  >
                    <select
                      id={`sched-map-${column.field}`}
                      className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-3 text-sm"
                      value={effectiveMapping[column.field] ?? ''}
                      onChange={(event) => changeMapping(column.field, event.target.value)}
                    >
                      <option value="">— não importar —</option>
                      {started.headers.map((header) => (
                        <option key={header} value={header}>
                          {header}
                        </option>
                      ))}
                    </select>
                  </Field>
                ))}
              </div>

              {missingRequired.length > 0 && (
                <p
                  role="alert"
                  className="flex items-center gap-2 text-sm text-(--color-state-danger)"
                >
                  <AlertTriangle className="size-4" aria-hidden="true" />
                  Falta indicar: {missingRequired.map((column) => column.label).join(', ')}.
                </p>
              )}

              <div>
                <h3 className="mb-2 text-sm font-medium">Primeiras linhas do arquivo</h3>
                <TableWrapper>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {started.headers.map((header) => (
                          <TableHead key={header}>{header}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {started.sample.map((row, index) => (
                        <TableRow key={index}>
                          {started.headers.map((header) => (
                            <TableCell key={header} className="max-w-40 truncate text-xs">
                              {row[header] ?? ''}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableWrapper>
              </div>

              <ImportOptions
                units={units}
                createStructure={createStructure}
                setCreateStructure={setCreateStructure}
                createMaterials={createMaterials}
                setCreateMaterials={setCreateMaterials}
                fallbackUnitId={fallbackUnitId}
                setFallbackUnitId={setFallbackUnitId}
              />
            </>
          )}

          {/* ── 3. Conferência ─────────────────────────────────────────────── */}
          {step === 2 && plan !== undefined && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                {(['ALL', 'VALID', 'WARNING', 'INVALID', 'DUPLICATE'] as const).map((value) => {
                  const count =
                    value === 'ALL'
                      ? plan.summary.total
                      : value === 'VALID'
                        ? plan.summary.valid
                        : value === 'WARNING'
                          ? plan.summary.warning
                          : value === 'INVALID'
                            ? plan.summary.invalid
                            : plan.summary.duplicate;
                  return (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={filter === value}
                      onClick={() => setFilter(value)}
                      className={cn(
                        'rounded-full border px-3 py-1 text-xs',
                        filter === value
                          ? 'border-(--color-brand) font-medium text-(--color-brand)'
                          : 'border-(--color-border-subtle) text-(--color-content-muted)',
                      )}
                    >
                      {value === 'ALL' ? 'Todas' : SCHEDULE_ROW_STATUS_LABEL[value]} ({count})
                    </button>
                  );
                })}
              </div>

              <p className="text-sm text-(--color-content-muted)">
                Serão criadas{' '}
                <strong className="text-(--color-content)">{plan.summary.createEntries}</strong>{' '}
                linhas e atualizadas{' '}
                <strong className="text-(--color-content)">{plan.summary.updateEntries}</strong>
                {plan.summary.newTowers +
                  plan.summary.newFloors +
                  plan.summary.newActivities +
                  plan.summary.newMaterials >
                  0 && <> — e mais {describeCreations(plan)}</>}
                .
              </p>

              <TableWrapper className="max-h-80 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-14">Linha</TableHead>
                      <TableHead>Situação</TableHead>
                      <TableHead>Torre</TableHead>
                      <TableHead>Pavimento</TableHead>
                      <TableHead>Disciplina</TableHead>
                      <TableHead>Material</TableHead>
                      <TableHead>Qtd.</TableHead>
                      <TableHead>Motivo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleRows.map((row) => (
                      <TableRow key={row.rowNumber}>
                        <TableCell className="tabular text-(--color-content-muted)">
                          {row.rowNumber}
                        </TableCell>
                        <TableCell>
                          <Badge tone={STATUS_TONE[row.status]}>
                            {SCHEDULE_ROW_STATUS_LABEL[row.status]}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-28 truncate">{row.raw.tower}</TableCell>
                        <TableCell className="max-w-28 truncate">{row.raw.floor}</TableCell>
                        <TableCell className="max-w-28 truncate">{row.raw.discipline}</TableCell>
                        <TableCell className="max-w-40 truncate">{row.raw.material}</TableCell>
                        <TableCell className="tabular">{row.raw.quantity}</TableCell>
                        <TableCell className="max-w-64 text-xs text-(--color-content-muted)">
                          {row.issues.map((issue) => issue.message).join(' ')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableWrapper>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <ImportOptions
                  units={units}
                  createStructure={createStructure}
                  setCreateStructure={setCreateStructure}
                  createMaterials={createMaterials}
                  setCreateMaterials={setCreateMaterials}
                  fallbackUnitId={fallbackUnitId}
                  setFallbackUnitId={setFallbackUnitId}
                  compact
                />
                {plan.summary.invalid + plan.summary.duplicate > 0 && (
                  <Button type="button" variant="ghost" size="sm" onClick={downloadErrors}>
                    Baixar linhas com erro
                  </Button>
                )}
              </div>
            </>
          )}

          {/* ── 4. Resultado ───────────────────────────────────────────────── */}
          {step === 3 && result !== undefined && (
            <div className="flex flex-col gap-2 rounded-md border border-(--color-state-ok) bg-(--color-surface-muted) p-4">
              <p className="font-medium">Importação concluída.</p>
              <ul className="text-sm text-(--color-content-muted)">
                <li>{result.createdEntries} linhas de cronograma criadas</li>
                <li>{result.updatedEntries} linhas atualizadas</li>
                {result.createdTowers > 0 && <li>{result.createdTowers} torres criadas</li>}
                {result.createdFloors > 0 && <li>{result.createdFloors} pavimentos criados</li>}
                {result.createdActivities > 0 && (
                  <li>{result.createdActivities} atividades criadas</li>
                )}
                {result.createdMaterials > 0 && (
                  <li>{result.createdMaterials} materiais criados no catálogo</li>
                )}
                {result.ignoredRows > 0 && <li>{result.ignoredRows} linhas ignoradas</li>}
              </ul>
            </div>
          )}

          {errorMessage !== undefined && (
            <p role="alert" className="text-sm text-(--color-state-danger)">
              {errorMessage}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            {step === 3 ? 'Fechar' : 'Cancelar'}
          </Button>

          {step === 1 && (
            <Button
              type="button"
              variant="primary"
              disabled={isPending || missingRequired.length > 0}
              onClick={() => onValidate(effectiveMapping, options)}
            >
              {isPending ? 'Conferindo…' : 'Conferir planilha'}
            </Button>
          )}

          {step === 2 && plan !== undefined && (
            <>
              <Button
                type="button"
                variant="secondary"
                disabled={isPending}
                onClick={() => onValidate(effectiveMapping, options)}
              >
                Reconferir
              </Button>
              <Button
                type="button"
                variant="primary"
                // RN-034 — zero linha válida não é importação.
                disabled={isPending || plan.summary.applicable === 0}
                onClick={onConfirm}
              >
                {isPending ? 'Importando…' : `Importar ${String(plan.summary.applicable)} linhas`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function describeCreations(plan: ScheduleImportPlan): string {
  const parts: string[] = [];
  if (plan.summary.newTowers > 0) parts.push(`${String(plan.summary.newTowers)} torre(s)`);
  if (plan.summary.newFloors > 0) parts.push(`${String(plan.summary.newFloors)} pavimento(s)`);
  if (plan.summary.newActivities > 0) {
    parts.push(`${String(plan.summary.newActivities)} atividade(s)`);
  }
  if (plan.summary.newMaterials > 0)
    parts.push(`${String(plan.summary.newMaterials)} material(is)`);
  return parts.join(', ');
}

/**
 * As duas opções que mudam o que a importação pode fazer.
 *
 * Ficam desligadas por padrão de propósito (RN-035, RN-010): criar estrutura e material a
 * partir de uma planilha é conveniente e perigoso — um erro de digitação vira uma torre
 * nova. Ligar é decisão consciente de quem importa.
 */
function ImportOptions({
  units,
  createStructure,
  setCreateStructure,
  createMaterials,
  setCreateMaterials,
  fallbackUnitId,
  setFallbackUnitId,
  compact = false,
}: {
  units: readonly UnitOfMeasure[];
  createStructure: boolean;
  setCreateStructure: (value: boolean) => void;
  createMaterials: boolean;
  setCreateMaterials: (value: boolean) => void;
  fallbackUnitId: string;
  setFallbackUnitId: (value: string) => void;
  compact?: boolean;
}) {
  return (
    <div className={cn('flex flex-col gap-2', compact ? 'text-xs' : 'text-sm')}>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={createStructure}
          onChange={(event) => setCreateStructure(event.target.checked)}
        />
        Criar torres e pavimentos que a planilha citar e a obra não tiver
      </label>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={createMaterials}
          onChange={(event) => setCreateMaterials(event.target.checked)}
        />
        Criar materiais novos no catálogo
      </label>
      {!compact && (
        <label className="flex items-center gap-2">
          Unidade padrão quando a linha não trouxer:
          <select
            aria-label="Unidade padrão da importação"
            className="h-8 rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 text-sm"
            value={fallbackUnitId}
            onChange={(event) => setFallbackUnitId(event.target.value)}
          >
            <option value="">— usar a do material —</option>
            {units.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.name}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}
